import { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { toast } from "sonner";
import { Building2, CheckCircle2 } from "lucide-react";
import { useSeo } from "../../lib/useSeo";

const EMPTY = {
  name: "", business_name: "", email: "", phone: "",
  gstin: "", city: "", monthly_volume: "", message: "",
};

export default function WholesaleEnquiry() {
  useSeo({
    title: "Request Wholesale Access · DailyBeans",
    description: "Apply for wholesale pricing on estate-sourced South Indian coffee. GST-registered cafés, offices and resellers welcome.",
    canonical: "https://mydailybean.com/wholesale/enquiry",
  });
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const upd = (k, v) => setForm(f => ({...f, [k]: v}));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.business_name || !form.email || !form.phone) {
      toast.error("Name, business, email and phone are required");
      return;
    }
    setBusy(true);
    try {
      await api.post("/wholesale/enquiries", form);
      setDone(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to submit. Please try again.");
    }
    setBusy(false);
  };

  if (done) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#FDFBF7] to-[#FEF3E2] flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl p-10 max-w-md w-full text-center shadow-2xl">
          <CheckCircle2 className="w-14 h-14 text-[#2E4A35] mx-auto mb-4"/>
          <h1 className="font-serif-display text-3xl text-[#3B2F2F] mb-2">Enquiry received</h1>
          <p className="text-sm text-stone-600 mb-6">
            Thanks {form.name}. Our wholesale team will review your request and get back to you within 24 hours on <b>{form.email}</b>.
          </p>
          <Link to="/" className="text-sm text-[#3B2F2F] underline hover:text-[#D4AF37]">Back to home</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#3B2F2F] to-[#2A2020] py-12 px-4">
      <div className="max-w-2xl mx-auto">
        <div className="text-center text-white mb-6">
          <div className="w-14 h-14 rounded-2xl bg-[#D4AF37] text-[#3B2F2F] flex items-center justify-center mx-auto mb-4">
            <Building2 className="w-7 h-7"/>
          </div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] mb-1">Wholesale enquiry</div>
          <h1 className="font-serif-display text-4xl">Grow your business with DailyBeans</h1>
          <p className="text-sm text-stone-300 mt-2 max-w-lg mx-auto">Cafés, offices, hotels, corporate gifting — tell us a bit about you and our team will send back a personalised wholesale quote within 24 hours.</p>
        </div>
        <form onSubmit={submit} className="bg-[#FDFBF7] rounded-2xl p-6 md:p-8 shadow-2xl space-y-4" data-testid="enquiry-form">
          <div className="grid md:grid-cols-2 gap-3">
            <Field label="Contact name*">
              <Input value={form.name} onChange={e=>upd("name", e.target.value)} data-testid="enq-name" required/>
            </Field>
            <Field label="Business / Company name*">
              <Input value={form.business_name} onChange={e=>upd("business_name", e.target.value)} data-testid="enq-business" required/>
            </Field>
            <Field label="Email*">
              <Input type="email" value={form.email} onChange={e=>upd("email", e.target.value.toLowerCase())} data-testid="enq-email" required/>
            </Field>
            <Field label="Phone*">
              <Input value={form.phone} onChange={e=>upd("phone", e.target.value)} data-testid="enq-phone" required/>
            </Field>
            <Field label="GSTIN (optional)">
              <Input value={form.gstin} onChange={e=>upd("gstin", e.target.value.toUpperCase())} data-testid="enq-gstin" placeholder="29AANCD0102E1Z3" className="font-mono"/>
            </Field>
            <Field label="City">
              <Input value={form.city} onChange={e=>upd("city", e.target.value)} data-testid="enq-city"/>
            </Field>
            <div className="md:col-span-2">
              <Field label="Estimated monthly volume (kg)">
                <Input value={form.monthly_volume} onChange={e=>upd("monthly_volume", e.target.value)} data-testid="enq-volume" placeholder="e.g. 25 kg / month"/>
              </Field>
            </div>
            <div className="md:col-span-2">
              <Field label="Tell us about your business (optional)">
                <Textarea rows={3} value={form.message} onChange={e=>upd("message", e.target.value)} data-testid="enq-message" placeholder="What kind of coffee do you serve? Which products interest you most?"/>
              </Field>
            </div>
          </div>
          <button type="submit" disabled={busy} data-testid="enq-submit"
                  className="w-full bg-[#3B2F2F] text-white rounded-md py-3 font-medium hover:bg-black disabled:bg-stone-400">
            {busy ? "Sending…" : "Send enquiry"}
          </button>
          <div className="text-center text-xs text-stone-500">
            Already have an account? <Link to="/wholesale/login" className="text-[#3B2F2F] underline">Sign in here</Link>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-[10px] uppercase tracking-widest text-stone-500 font-outfit font-semibold mb-1.5">{label}</div>
      {children}
    </label>
  );
}
